import React, { useState, useEffect } from 'react';
import { AlertCircle } from 'lucide-react';
import { wholesaleOrders } from '../services/api';
import { useCart } from '../contexts/CartContext';

const fmt = (n) =>
  '$' + parseFloat(n || 0).toLocaleString('en-US', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  });

/**
 * ReorderModal
 * Props:
 *   orderId  — number|null  (null = closed)
 *   onClose  — () => void
 *   onSuccess — (count: number) => void   called after items are added to cart
 */
export default function ReorderModal({ orderId, onClose, onSuccess }) {
  const { cart, addToCart } = useCart();

  const [items,   setItems]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState('');
  const [qtys,    setQtys]    = useState({});     // { productID: qty }
  const [checked, setChecked] = useState({});     // { productID: bool }

  const hasCartItems = Object.keys(cart).length > 0;

  useEffect(() => {
    if (!orderId) return;
    setLoading(true);
    setError('');
    setItems([]);
    wholesaleOrders
      .checkReorder(orderId)
      .then((res) => {
        const data = res.data.data || [];
        setItems(data);
        const initQtys    = {};
        const initChecked = {};
        data.forEach((item) => {
          initQtys[item.productID]    = item.originalQty;
          initChecked[item.productID] = item.isAvailable; // pre-check available, uncheck unavailable
        });
        setQtys(initQtys);
        setChecked(initChecked);
      })
      .catch((err) =>
        setError(err?.response?.data?.message || 'Failed to load item availability.')
      )
      .finally(() => setLoading(false));
  }, [orderId]);

  const handleAddToCart = () => {
    let count = 0;
    Object.entries(checked).forEach(([productId, isChecked]) => {
      if (!isChecked) return;
      const qty = parseInt(qtys[productId], 10) || 0;
      if (qty > 0) {
        addToCart(parseInt(productId, 10), qty);
        count++;
      }
    });
    onSuccess(count);
    onClose();
  };

  const selectedCount = Object.values(checked).filter(Boolean).length;

  if (!orderId) return null;

  return (
    <div
      className="modal fade show d-block"
      tabIndex="-1"
      style={{ background: 'rgba(0,0,0,.5)' }}
      onClick={(e) => e.target === e.currentTarget && !loading && onClose()}
    >
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Reorder from Order #{orderId}</h5>
            <button type="button" className="btn-close" onClick={onClose} />
          </div>

          <div className="modal-body">
            {loading && (
              <div className="text-center py-5">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading…</span>
                </div>
              </div>
            )}

            {!loading && error && (
              <div className="alert alert-danger">{error}</div>
            )}

            {!loading && !error && (
              <>
                {hasCartItems && (
                  <div className="alert alert-warning d-flex align-items-center gap-2 py-2 mb-3">
                    <AlertCircle size={16} className="flex-shrink-0" />
                    <span className="small">
                      You already have items in your cart. Selecting items here will
                      add to your existing cart.
                    </span>
                  </div>
                )}

                {items.length === 0 ? (
                  <p className="text-muted text-center py-4">No items found in this order.</p>
                ) : (
                  <div className="table-responsive">
                    <table className="table table-sm table-hover align-middle mb-0">
                      <thead className="table-light">
                        <tr>
                          <th style={{ width: 36 }}></th>
                          <th>Product</th>
                          <th className="text-center" style={{ width: 90 }}>Qty</th>
                          <th className="text-end">Orig. Price</th>
                          <th className="text-end">Current Price</th>
                          <th style={{ width: 110 }}>Available</th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item) => (
                          <tr
                            key={item.productID}
                            className={!item.isAvailable ? 'opacity-50' : ''}
                          >
                            {/* Checkbox */}
                            <td>
                              <input
                                type="checkbox"
                                className="form-check-input"
                                checked={!!checked[item.productID]}
                                onChange={(e) =>
                                  setChecked((prev) => ({
                                    ...prev,
                                    [item.productID]: e.target.checked,
                                  }))
                                }
                                disabled={!item.isAvailable}
                              />
                            </td>

                            {/* Product name */}
                            <td>
                              <div className="fw-semibold small">{item.name}</div>
                              {(item.vintage || item.varietal) && (
                                <div className="text-muted" style={{ fontSize: 11 }}>
                                  {[item.vintage, item.varietal].filter(Boolean).join(' · ')}
                                </div>
                              )}
                            </td>

                            {/* Qty stepper */}
                            <td className="text-center">
                              {item.isAvailable ? (
                                <input
                                  type="number"
                                  className="form-control form-control-sm text-center"
                                  style={{ width: 70, margin: '0 auto' }}
                                  min={1}
                                  value={qtys[item.productID] ?? item.originalQty}
                                  onChange={(e) =>
                                    setQtys((prev) => ({
                                      ...prev,
                                      [item.productID]: e.target.value,
                                    }))
                                  }
                                />
                              ) : (
                                <span className="text-muted small">{item.originalQty}</span>
                              )}
                            </td>

                            {/* Original price */}
                            <td className="text-end small">{fmt(item.originalPrice)}</td>

                            {/* Current price */}
                            <td className="text-end small">
                              {item.isAvailable ? (
                                <span
                                  className={item.priceChanged ? 'text-warning fw-semibold' : ''}
                                  title={item.priceChanged ? 'Price has changed since your original order' : ''}
                                >
                                  {fmt(item.currentPrice)}
                                  {item.priceChanged && (
                                    <span
                                      className="ms-1"
                                      style={{ cursor: 'help' }}
                                      aria-label="Price has changed"
                                    >
                                      ⚠
                                    </span>
                                  )}
                                </span>
                              ) : (
                                <span className="text-muted">—</span>
                              )}
                            </td>

                            {/* Availability badge */}
                            <td>
                              {item.isAvailable ? (
                                <span className="badge bg-success">Available</span>
                              ) : (
                                <span
                                  className="badge bg-secondary"
                                  title={item.unavailableReason || 'Unavailable'}
                                >
                                  Unavailable
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </div>

          <div className="modal-footer">
            <button
              type="button"
              className="btn btn-outline-secondary"
              onClick={onClose}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleAddToCart}
              disabled={loading || selectedCount === 0}
            >
              Add{' '}
              {selectedCount > 0
                ? `${selectedCount} Item${selectedCount !== 1 ? 's' : ''}`
                : 'Selected'}{' '}
              to Cart
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
